import { useContext, useState, useCallback } from 'react';
import { AppContext } from 'contexts/AppContext';
import { apiPost } from './apiPost';

/**
 * Data access for the Overview tab.
 *
 * /send/overview-summary  → headline figures + period-over-period deltas
 * /send/daily-trend       → per-day volume for the chart
 * /send/tool-performance  → REUSED from Quality, not duplicated
 *
 * Tool mix deliberately calls Quality's endpoint rather than getting its own.
 * Two endpoints computing the same figure is two endpoints that can disagree
 * about it, and the coarser presentation Overview wants is a rendering choice,
 * not a different query.
 *
 * `funnel` and `widget_opens` come back null and stay null — there is no event
 * for a user opening the assistant.
 */

export const useOverviewData = () => {
  const { dateRange, selectedClient, getAccessTokenSilently, base_url, platformFilter, environmentFilter, languageFilter } = useContext(AppContext);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  const [trend, setTrend] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);

  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  const [tools, setTools] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);

  const post = useCallback(
    (path, body) => apiPost(base_url, getAccessTokenSilently, path, body),
    [base_url, getAccessTokenSilently],
  );

  // dateRange is an ARRAY [from, to] of Dates, not {from,to} — AppContext.js:152.
  const range = useCallback(() => {
    const [from, to] = Array.isArray(dateRange) ? dateRange : [];
    if (!from || !to || !selectedClient?.key) return null;
    return {
      start_date: new Date(from).toISOString().split('T')[0],
      end_date: new Date(to).toISOString().split('T')[0],
      client: selectedClient.key,
      // Sent from one place so no individual fetch can forget it. Endpoints
      // that cannot honour it ignore it and report so in their response.
      ...(platformFilter ? { platform: platformFilter } : {}),
      // Always explicit -- 'prod' | 'stage' | 'all' -- so what the buttons show
      // is exactly what the query ran with. Absent would also mean 'prod' (the
      // backend default), but an implicit default is how filters get dropped.
      environment: environmentFilter || 'all',
      ...(languageFilter ? { language: languageFilter } : {}),
    };
  }, [dateRange, selectedClient, platformFilter, environmentFilter, languageFilter]);

  const fetchSummary = useCallback(async ({ excludeInternal = false, internalOnly = false } = {}) => {
    const base = range();
    if (!base) return;
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const data = await post('overview-summary', {
        ...base,
        ...(excludeInternal ? { exclude_internal: true } : {}),
        ...(internalOnly ? { internal_only: true } : {}),
      });
      setSummary(data?.data || null);
    } catch (err) {
      setSummaryError(err.response?.data?.detail || err.message || 'Failed to load overview');
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [range, post]);

  const fetchTrend = useCallback(async ({ excludeInternal = false, internalOnly = false } = {}) => {
    const base = range();
    if (!base) return;
    setTrendLoading(true);
    try {
      const data = await post('daily-trend', {
        ...base,
        ...(excludeInternal ? { exclude_internal: true } : {}),
        ...(internalOnly ? { internal_only: true } : {}),
      });
      setTrend(data?.data || []);
    } catch {
      setTrend([]);
    } finally {
      setTrendLoading(false);
    }
  }, [range, post]);

  const fetchTools = useCallback(async ({ excludeInternal = false, internalOnly = false } = {}) => {
    const base = range();
    if (!base) return;
    setToolsLoading(true);
    try {
      const data = await post('tool-performance', {
        ...base,
        ...(excludeInternal ? { exclude_internal: true } : {}),
        ...(internalOnly ? { internal_only: true } : {}),
      });
      setTools(data?.data || []);
    } catch {
      setTools([]);
    } finally {
      setToolsLoading(false);
    }
  }, [range, post]);

  // Liveness, not uptime -- and no date/platform filter, because "is it up" has
  // nothing to do with which window you are looking at.
  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const data = await post('endpoint-health', {});
      setHealth(data?.data || null);
    } catch {
      setHealth(null);
    } finally {
      setHealthLoading(false);
    }
  }, [post]);

  return {
    health, healthLoading, fetchHealth,
    summary, summaryLoading, summaryError, fetchSummary,
    trend, trendLoading, fetchTrend,
    tools, toolsLoading, fetchTools,
  };
};

export default useOverviewData;
